import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../../../api/document';
import { QualioDocumentAttachmentPreviewType } from '../../../../../../api/model/document';
import { qualioAttachmentService } from '../../../../../../api/qualioAttachmentService';
import { getApiErrorData } from '../../../../../../util/ApiErrorUtils';
import { getFileAttachmentFromFileUploadDocument } from '../../../../../../util/FileUploadUtils';
import { DocumentOverviewContext } from '../../../Context';
import { shouldRenderRegeneratePreviewButton } from '../../../RenderLogic';

const ATTACHMENT_PREVIEW_NOT_APPLICABLE_SLUG =
  'attachment_preview_not_applicable';

type RegeneratePreviewButtonProps = {
  renderClickableItem: (
    onClick: () => void,
    isDisabled: boolean,
  ) => JSX.Element;
};

export const RegeneratePreviewButton = ({
  renderClickableItem,
}: RegeneratePreviewButtonProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();

  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );

  const [isDisabled, setIsDisabled] = useState(false);

  const attachment = getFileAttachmentFromFileUploadDocument(qualioDocument);

  const regeneratePreview = async (attachmentId: string) => {
    try {
      await documentApi.regenerateAttachmentPreview(attachmentId);
      showToast({
        'data-cy': 'attachment-preview-regeneration-initiated',
        status: 'info',
        title: t('notifications.regenerationInitiated'),
        description: t('notifications.regenerationInitiatedDescription'),
        id: 'regenerate-attachment-preview-toast',
        replace: true,
        duration: 10000,
      });

      await qualioAttachmentService.pollAttachmentInfoUntilCompletion(
        attachmentId,
      );
      refetchDocumentOverviewData();
      setIsDisabled(false);
    } catch (e: unknown) {
      const { slug } = getApiErrorData(e);

      if (slug === ATTACHMENT_PREVIEW_NOT_APPLICABLE_SLUG) {
        showToast({
          'data-cy': 'attachment-preview-regeneration-not-applicable',
          status: 'error',
          title: t('notifications.regenerationNotApplicable'),
          description: t('notifications.regenerationNotApplicableDescription'),
          id: 'regenerate-attachment-preview-toast',
          replace: true,
          duration: 10000,
        });
      } else {
        showToast({
          'data-cy': 'attachment-preview-regeneration-error',
          status: 'error',
          title: t('notifications.regenerationFailed'),
          id: 'regenerate-attachment-preview-toast',
          replace: true,
          duration: 10000,
        });
      }
      setIsDisabled(false);
    } finally {
      showToast({
        'data-cy': 'attachment-preview-regeneration-success',
        status: 'success',
        title: t('notifications.success'),
        description: t('notifications.regenerationSuccess'),
        id: 'regenerate-attachment-preview-toast',
        replace: true,
        duration: 10000,
      });
    }
  };

  const isDocumentTypeAttachment =
    attachment?.preview_type === QualioDocumentAttachmentPreviewType.DOCUMENT;

  if (
    !attachment ||
    !isDocumentTypeAttachment ||
    !shouldRenderRegeneratePreviewButton(
      qualioDocument,
      attachment,
      currentUser,
    )
  ) {
    return null;
  }

  return renderClickableItem(() => {
    setIsDisabled(true);
    void regeneratePreview(String(attachment.id));
  }, isDisabled);
};
